import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DatePicker, Form, Input, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { Moment } from "moment";
import { v4 } from "uuid";
import getValidationErrors from "../../../../utils/getValidationErrors";
import FormItem from "../../../../components/FormItem";
import { useToast } from "../../../../hooks/toast";
import api from "../../../../services/api";
import { removeNullKeys } from "../../../../utils/formatters";
import { IUser } from "../../../Users";
import { IDetailedProject, IRequirement } from "../..";
import ActivityTypes from "../../../../enums/ActivityTypes";
import ProjectTypes from "../../../../enums/ProjectTypes";
import filterOptions from "../../../../utils/filterOptions";
import ProjectStatus from "../../../../enums/ProjectStatus";
import ITypePricingProject from "../../../../interfaces/ITypePricingProject";
import { useAuth } from "../../../../hooks/auth";
import UserRoles from "../../../../enums/UserRoles";

interface IActivityModalProps {
  visible: boolean;
  project: IDetailedProject;
  projectType?: ProjectTypes;
  handleCloseModal: (createdActivity?: Partial<IRequirement>) => void;
}

interface IHoursFactory {
  description: string;
  id: string;
  value_hour: number;
}

interface IActivityFormData {
  identifier: string;
  description: string;
  hours?: number;
  type: string;
  consultant_id?: string;
  project_id: string;
  allocation_month: Moment;
  estimator_id: string;
  expected_date_begin?: string;
  expected_date_end?: string;
  hour_factory?: ITypePricingProject;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const ActivityModal: React.FC<IActivityModalProps> = ({
  visible,
  project,
  projectType,
  handleCloseModal,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [users, setUsers] = useState<IUser[]>([]);
  const [userFilter, setUserFilter] = useState<string>();
  const [hoursFactory, setHoursFactory] = useState<IHoursFactory[]>([]);

  const { t } = useTranslation(["activities"]);
  const { addToast } = useToast();
  const [form] = Form.useForm();
  const { user } = useAuth();

  const projectHoursSeniority = useMemo(() => {
    return project?.pricing === ITypePricingProject.HoursSeniority;
  }, [project]);

  const optionsHoursFactory = useMemo(() => {
    return hoursFactory?.map(hour => ({
      label: hour.description,
      value: hour.id,
    }));
  }, [hoursFactory]);

  const ProjectHourSeniority = useMemo(() => {
    return project?.pricing === ITypePricingProject.HoursSeniority;
  }, [project]);

  useEffect(() => {
    if (projectHoursSeniority) {
      api
        .get<IHoursFactory[]>("config_hours_allocation")
        .then(resp => setHoursFactory(resp.data));
    }
  }, [projectHoursSeniority]);

  useEffect(() => {
    if (visible) {
      setValidationErrors({});
    }
  }, [visible]);

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          order_name: "name",
          order_direction: "ASC",
        },
      })
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAllocation = useMemo(() => projectType === ProjectTypes.Allocation, [
    projectType,
  ]);

  const usersOptions = useMemo(() => {
    if (!users) {
      return undefined;
    }

    if (project.public_project && user.role === UserRoles.Developer) {
      return users
        .filter(usr => usr.id === user.id)
        .map(usr => ({
          label: usr.name,
          value: usr.id,
        }));
    }

    if (isAllocation) {
      const user_id = project?.requirements[0]?.consultant_id || "";
      return users
        .filter(usr => usr.id === user_id)
        .map(usr => ({
          label: usr.name,
          value: usr.id,
        }));
    }

    return users.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [users, isAllocation, project]);

  const activityTypeOptions = useMemo(() => {
    if (project.public_project) {
      return [
        {
          label: t(`types.options.${ActivityTypes.Eventual}.label`),
          value: ActivityTypes.Eventual,
        },
      ];
    }
    return (Object.keys(ActivityTypes) as (keyof typeof ActivityTypes)[])
      .filter(key => ActivityTypes[key] !== ActivityTypes.Eventual)
      .map(key => {
        return {
          label: t(`types.options.${ActivityTypes[key]}.label`),
          value: ActivityTypes[key],
        };
      });
  }, [t, project.public_project]);

  const handleClose = useCallback(
    (createdActivity?: Partial<IRequirement>) => {
      form.resetFields();
      handleCloseModal(createdActivity);
    },
    [form, handleCloseModal],
  );

  const isFactory = useMemo(() => projectType === ProjectTypes.Factory, [
    projectType,
  ]);

  const projectStatus = useMemo(() => {
    return (
      project?.status === ProjectStatus.WaitingDevelopment ||
      project?.status === ProjectStatus.Development
    );
  }, [project]);

  const handleSubmit = useCallback(
    async (formData: IActivityFormData) => {
      const data = removeNullKeys(formData);
      try {
        const schema = Yup.object().shape({
          isAllocation: Yup.boolean(),
          project_id: Yup.string().required("ID do projeto obrigatório"),
          hours: Yup.string().required("Horas obrigatórias"),
          estimator_id: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Estimador obrigatório"),
          }),
          identifier: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Identificador obrigatório"),
          }),
          description: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Descrição obrigatória"),
          }),
          allocation_month: Yup.date().when("isAllocation", {
            is: true,
            then: Yup.date().required("Mês de alocação obrigatório"),
          }),
          type: Yup.string().when("isAllocation", {
            is: false,
            then: Yup.string().required("Tipo de atividade obrigatório"),
          }),
          expected_date_begin: Yup.date().when("isAllocation", (_, field) => {
            return isFactory && projectStatus
              ? field.required("data de início obrigatório")
              : field;
          }),
          expected_date_end: Yup.date().when("isAllocation", (_, field) => {
            return isFactory && projectStatus
              ? field.required("data de fim obrigatório")
              : field;
          }),
          consultant_id: Yup.string().when("isAllocation", (_, field) => {
            return projectStatus
              ? field.required("Codnsultor obrigatório")
              : field;
          }),
          hour_factory: Yup.string().when("isAllocation", (_, field) => {
            return projectHoursSeniority
              ? field.required("Obrigatorio selecionar um perfil")
              : field;
          }),
        });

        await schema.validate(
          { ...data, isAllocation, consultant_id: userFilter },
          {
            abortEarly: false,
          },
        );

        setLoading(true);

        const {
          project_id,
          identifier,
          description,
          hours,
          type,
          allocation_month,
          estimator_id,
          expected_date_begin,
          expected_date_end,
          hour_factory,
        } = data;

        const payload = ({
          project_id,
          id: v4(),
          consultant_id: userFilter,
          identifier,
          description,
          month: allocation_month && allocation_month.month(),
          year: allocation_month && allocation_month.year(),
          hours: Number(hours),
          type: isAllocation ? "ALLOCATION_MONTH" : type,
          estimator_id,
          expected_date_begin: expected_date_begin || null,
          expected_date_end: expected_date_end || null,
          config_hour_factory_id: hour_factory,
        } as unknown) as Partial<IRequirement>;
        addToast({
          type: "info",
          title: "Atividade pendete adicionada",
          description: "As atividades pendentes precisam ser confirmadas.",
        });

        handleClose(payload);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      handleClose,
      isAllocation,
      isFactory,
      projectStatus,
      userFilter,
      projectHoursSeniority,
    ],
  );

  useEffect(() => {
    form.setFieldsValue({
      project_id: project.id,
    });
  }, [form, project]);

  const handleSelectUser = useCallback(usr => setUserFilter(usr), []);

  return (
    <Modal
      title={t("actions.options.create")}
      visible={visible}
      width={ProjectHourSeniority ? 900 : 600}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        initialValues={{
          project_id: project.id,
        }}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        {isAllocation ? (
          <>
            <FormItem name="project_id" hidden>
              <Input />
            </FormItem>
            <Form.Item
              name="allocation_month"
              required
              label={t("allocation_month")}
            >
              <DatePicker
                picker="month"
                style={{ width: "100%" }}
                format="MMMM YYYY"
              />
            </Form.Item>
            <FormItem
              name="consultant_id"
              required={projectStatus}
              label={t("consultant")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                onChange={handleSelectUser}
                options={usersOptions}
                placeholder={t("consultant")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
          </>
        ) : (
          <>
            <FormItem name="project_id" hidden>
              <Input />
            </FormItem>
            <FormItem
              name="type"
              required
              label={t("type")}
              errors={validationErrors}
            >
              <Select allowClear options={activityTypeOptions} />
            </FormItem>
            <FormItem
              name="identifier"
              required
              label={t("identifier")}
              errors={validationErrors}
            >
              <Input />
            </FormItem>

            <FormItem
              name="description"
              required
              label={t("description")}
              errors={validationErrors}
            >
              <Input />
            </FormItem>
            {/* <Form.Item name="consultant_id" label={t("consultant")}>
              <Select
                allowClear
                showSearch
                options={usersOptions}
                loading={loadingUsers}
              />
            </Form.Item> */}
            <FormItem
              name="consultant_id"
              label={t("consultant")}
              errors={validationErrors}
              required={projectStatus}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                onChange={handleSelectUser}
                options={usersOptions}
                placeholder={t("consultant")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
            {projectHoursSeniority ? (
              <>
                <FormItem
                  name="hour_factory"
                  label="Perfil"
                  required={projectHoursSeniority}
                  errors={validationErrors}
                >
                  <Select
                    allowClear
                    showSearch
                    style={{ width: "100%" }}
                    placeholder={t("selecione")}
                    filterOption={filterOptions}
                    options={optionsHoursFactory}
                  />
                </FormItem>
              </>
            ) : (
              <></>
            )}
            <FormItem
              name="estimator_id"
              required
              label={t("Estimador")}
              errors={validationErrors}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: "100%" }}
                filterOption={filterOptions}
                options={usersOptions}
                placeholder={t("Estimador")}
                loading={loadingUsers}
                value={userFilter}
              />
            </FormItem>
          </>
        )}
        <FormItem
          name="hours"
          required
          label={t("Horas")}
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        {projectStatus && isFactory ? (
          <>
            <FormItem
              name="expected_date_begin"
              label={t("data de início")}
              errors={validationErrors}
              required={isFactory && projectStatus}
            >
              <DatePicker style={{ width: "100%" }} />
            </FormItem>
            <FormItem
              name="expected_date_end"
              label={t("data de fim")}
              errors={validationErrors}
              required={isFactory && projectStatus}
            >
              <DatePicker style={{ width: "100%" }} />
            </FormItem>
          </>
        ) : (
          <></>
        )}
      </Form>
    </Modal>
  );
};

export default ActivityModal;
